import { useMemo, useState } from "react";
import { Card } from "../../../../shadcn/components/ui/card";
import { PDAskUserMissingDetailsModal } from "./PDAskUserMissingDetailsModal";
import {
  PropertyType,
  useBidInterestApi,
} from "../../../../hoc/useBidInterestApi";
import { useAsyncAction } from "../../../../hoc/useAsyncAction";
import { PDDownloadAllCtaButton } from "./PDDownloadAllCtaButton";
import { PDDownloadAllSuccessModal } from "./PDDownloadAllSuccessModal";
import { PDDocumentPreviewModal } from "./PDDocumentPreviewModal";
import { useMissingUserDetailsControls } from "../../../../hoc/useMissingUserDetailsControls";
import { useDownloadAllCta } from "../hooks/useDownloadAllCta";
import { useDownloadDocumentApi } from "../../../../hoc/useDownloadDocumentApi";
import { PDDocumentsList } from "./PDDocumentsList";
import { useWithAuth } from "../../../../hoc/useWithAuth";

interface PDDocumentsViewPropsInterface {
  propertyData: any;
  setAuthModelOpen: (open: boolean) => void;
  propertyType: PropertyType;
  title?: string;
}

export const PDDocumentsView: React.FC<PDDocumentsViewPropsInterface> = ({
  propertyData,
  setAuthModelOpen = () => {},
  propertyType = "auction_property",
  title = "Documents",
}) => {
  const [openPreviewModal, setPreviewModalOpen] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [
    openDownloadAllAsZipSuccessModal,
    setOpenDownloadAllAsZipSuccessModal,
  ] = useState(false);

  const propertyDocuments = useMemo(
    () =>
      propertyData?.documents?.map((doc: any, index: number) => {
        const url = doc?.path_to_file;
        const path = new URL(url).pathname.slice(1);

        return {
          title: doc?.file_name,
          fileUuid: doc?.uuid,
          previewUrl: `${process.env.NEXT_PUBLIC_IMAGIX_URL}/${path}`,
          downloadUrl: doc?.download_url,
          id: `auction_property_document_${doc?.uuid}_${index}`,
        };
      }),
    [propertyData?.documents],
  );

  const { withAuth } = useWithAuth({
    openAuthModel: () => setAuthModelOpen(true),
  });

  const {
    missingFields,
    openMissingDetailsModal,
    setOpenMissingDetailsModal,
    askUserDetailsModalInitiatedFrom,
    setAskUserMissingDetailsModalInitiatedFrom,
    handleOpenCollectMissingDetailsPopup,
    missingDetails,
    setMissingDetails,
    resolvedMobile,
    resolvedEmail,
    resolvedName,
  } = useMissingUserDetailsControls();

  const { execute: triggerDownloadPropertyDocumentApi } =
    useDownloadDocumentApi();
  const { execute: triggerBidInterestApi } = useBidInterestApi();

  const {
    execute: handleSendSingleDocumentsToUserWhatsApp,
    isLoading: isSendingSingleDocumentToWhatsApp,
    isSuccess: isSendingSingleDocumentToWhatsAppSuccess,
    isError: isSendingSingleDocumentToWhatsAppError,
    reset: resetSendToWhatsAppButtonStates,
  } = useAsyncAction(async () => {
    triggerDownloadPropertyDocumentApi(
      {
        propertyUuid: propertyData?.uuid,
        userName: resolvedName,
        userEmail: resolvedEmail,
        userMobile: resolvedMobile,
        singleFileDownload: true,
        fileId: selectedDoc?.fileUuid,
      },
      {
        onSuccess: () => {},
        onError: () => {},
      },
    );

    triggerBidInterestApi({
      propertyType,
      requestType: `send_single_${propertyType}_document_to_user_whatsapp_mobile`,
      propertyListingId: propertyData?.listing_id,
      remark: "",
      userMobile: resolvedMobile,
      userEmail: resolvedEmail,
      userName: resolvedName,
    });
  });

  const {
    execute: handleSendAllDocumentsToUserWhatsApp,
    isLoading: isSendingAllDocumentsToWhatsApp,
    isSuccess: isSendingAllDocumentsToWhatsAppSuccess,
    isError: isSendingAllDocumentsToWhatsAppError,
  } = useAsyncAction(async () => {
    triggerDownloadPropertyDocumentApi(
      {
        propertyUuid: propertyData?.uuid,
        userName: resolvedName,
        userEmail: resolvedEmail,
        userMobile: resolvedMobile,
        singleFileDownload: false,
      },
      {
        onSuccess: () => {},
        onError: () => {},
      },
    );
    triggerBidInterestApi({
      propertyType,
      requestType: `send_all_${propertyType}_documents_to_user_whatsapp_mobile`,
      propertyListingId: propertyData?.listing_id,
      remark: "",
      userMobile: resolvedMobile,
      userEmail: resolvedEmail,
      userName: resolvedName,
    });
  });

  const handleSubmitMobile = () => {
    if (
      askUserDetailsModalInitiatedFrom ===
      `send_all_${propertyType}_documents_to_user_whatsapp_mobile`
    ) {
      handleSendAllDocumentsToUserWhatsApp({});
      return;
    }

    if (
      askUserDetailsModalInitiatedFrom ===
      `send_single_${propertyType}_document_to_user_whatsapp_mobile`
    ) {
      handleSendSingleDocumentsToUserWhatsApp({});
      return;
    }
  };

  const handleViewDocument = () => {
    setPreviewModalOpen(true);
    triggerBidInterestApi({
      propertyType,
      requestType: `view_${propertyType}_document`,
      propertyListingId: propertyData?.listing_id,
      remark: "",
      userMobile: resolvedMobile,
      userEmail: resolvedEmail,
      userName: resolvedName,
    });
  };

  const guardedViewDocument = withAuth(handleViewDocument);

  const handleViewDocumentClick = (document: any) => {
    setSelectedDoc(document);
    guardedViewDocument();
  };

  const handleDownloadDocument = () => {
    triggerBidInterestApi({
      propertyType,
      requestType: `download_${propertyType}_document`,
      propertyListingId: propertyData?.listing_id,
      remark: "",
      userMobile: resolvedMobile,
      userEmail: resolvedEmail,
      userName: resolvedName,
    });
    if (resolvedMobile) {
      handleSendSingleDocumentsToUserWhatsApp({});
    }
  };

  const guardedHandleDownloadDocument = withAuth(handleDownloadDocument);

  const {
    execute: handleDownloadDocumentClick,
    isLoading: isDownloadingDocument,
    isSuccess: isDownloadDocumentSuccess,
    isError: isDownloadDocumentError,
    reset: resetDownloadDocumentButtonStates,
  } = useAsyncAction(async () => {
    guardedHandleDownloadDocument();
  });

  const {
    handleDownloadAllDocumentsWhenUserMobileExist,
    handleDownloadAllDocumentsWhenUserMobileNotExists,
  } = useDownloadAllCta({
    onDownloadAllDocumentsWhenUserMobileNotExistsCallback: () => {
      setOpenDownloadAllAsZipSuccessModal(true);
    },
  });

  const {
    execute: handleDownloadDocumentsAsZipSuccess,
    isLoading: isDownloadDocumentsAsZipSuccessLoading,
    isSuccess: isDownloadDocumentsAsZipSuccessSuccess,
    isError: isDownloadDocumentsAsZipSuccessError,
  } = useAsyncAction(async () => {
    if (resolvedMobile) {
      handleDownloadAllDocumentsWhenUserMobileExist({
        propertyUuid: propertyData?.uuid,
        resolvedName,
        resolvedMobile,
        resolvedEmail,
        propertyType,
        propertyListingId: propertyData?.listing_id,
      });
    } else {
      handleDownloadAllDocumentsWhenUserMobileNotExists({
        resolvedName,
        resolvedMobile,
        resolvedEmail,
        propertyType,
        propertyListingId: propertyData?.listing_id,
      });
    }
  });

  return propertyDocuments?.length > 0 ? (
    <div>
      <Card className="space-y-3 bg-white shadow-card-shadow p-4 border-none rounded-[10px]">
        <div className="flex flex-wrap justify-between gap-2">
          <h3 className="text-lg font-openSans font-semibold text-brand-textBlack capitalize">
            {title}
          </h3>

          {propertyDocuments?.length > 1 && (
            <PDDownloadAllCtaButton
              files={propertyDocuments}
              zipFileName={propertyData?.name}
              onDownloadSuccess={() => handleDownloadDocumentsAsZipSuccess({})}
              withAuth={withAuth}
              buttonStates={{
                processing: isDownloadDocumentsAsZipSuccessLoading,
                success: isDownloadDocumentsAsZipSuccessSuccess,
                error: isDownloadDocumentsAsZipSuccessError,
              }}
            />
          )}
        </div>

        <PDDocumentsList
          propertyDocuments={propertyDocuments}
          propertyData={propertyData}
          onDocumentView={(document) => {
            handleViewDocumentClick(document);
          }}
          viewDocumentButtonStates={{
            processing: false,
            success: false,
            error: false,
          }}
        />
      </Card>
      <PDDownloadAllSuccessModal
        openDownloadAllAsZipSuccess={openDownloadAllAsZipSuccessModal}
        setOpenDownloadAllAsZipSuccess={setOpenDownloadAllAsZipSuccessModal}
        onClose={() => {
          setOpenDownloadAllAsZipSuccessModal(false);
        }}
        onWhatsAppButtonClick={() =>
          handleOpenCollectMissingDetailsPopup(
            `send_all_${propertyType}_documents_to_user_whatsapp_mobile`,
          )
        }
        whatsAppButtonStates={{
          processing: isSendingAllDocumentsToWhatsApp,
          success: isSendingAllDocumentsToWhatsAppSuccess,
          error: isSendingAllDocumentsToWhatsAppError,
        }}
      />

      <PDDocumentPreviewModal
        openPreviewModal={openPreviewModal}
        documentTitle={selectedDoc?.title}
        documentDownloadUrl={selectedDoc?.downloadUrl}
        documentPreviewUrl={selectedDoc?.previewUrl}
        showWhatsAppButton={Boolean(resolvedMobile) === false}
        handleClosePreviewModal={() => {
          setPreviewModalOpen(false);
          resetDownloadDocumentButtonStates();
          resetSendToWhatsAppButtonStates();
        }}
        onDownloadDocumentCtaClick={() => handleDownloadDocumentClick({})}
        onWhatsAppButtonClick={() => {
          handleOpenCollectMissingDetailsPopup(
            `send_single_${propertyType}_document_to_user_whatsapp_mobile`,
          );
        }}
        downloadDocumentButtonStates={{
          processing: isDownloadingDocument,
          success: isDownloadDocumentSuccess,
          error: isDownloadDocumentError,
        }}
        whatsAppButtonStates={{
          processing: isSendingSingleDocumentToWhatsApp,
          success: isSendingSingleDocumentToWhatsAppSuccess,
          error: isSendingSingleDocumentToWhatsAppError,
        }}
      />

      <PDAskUserMissingDetailsModal
        open={openMissingDetailsModal}
        onClose={() => {
          setOpenMissingDetailsModal(false);
          setAskUserMissingDetailsModalInitiatedFrom("na");
          setMissingDetails({ mobile: "" });
        }}
        config={{
          fields: missingFields,
          title: "Complete your details",
          description: "These details are required to continue.",
        }}
        values={missingDetails}
        setValue={(field, value) => {
          setMissingDetails((prev) => ({
            ...prev,
            [field]: value,
          }));
        }}
        onSubmit={(value) => {
          setMissingDetails(value);
          handleSubmitMobile();
          setOpenMissingDetailsModal(false);
          setAskUserMissingDetailsModalInitiatedFrom("na");
          setMissingDetails({ mobile: "" });
        }}
      />
    </div>
  ) : null;
};
